import { useMutation } from '@apollo/client';
import type {
  InsertDepartmentTypeGroupByNameMutation,
  InsertDepartmentTypeGroupByNameMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertDepartmentTypeGroupByNameDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useInsertDepartmentTypeGroupByNameTRPC } from './useInsertDepartmentTypeGroupByNameTRPC';

export const useInsertDepartmentTypeGroupByName = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [insertDepartmentTypeGroupByNameGraphQL, graphqlState] = useMutation(
    InsertDepartmentTypeGroupByNameDocument,
    {
      update: (cache) => {
        evictField(cache, 'department_type_group');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertDepartmentTypeGroupByNameTRPC();

  const insertDepartmentTypeGroupByName = async (
    variables: InsertDepartmentTypeGroupByNameMutationVariables
  ): Promise<InsertDepartmentTypeGroupByNameMutation> => {
    if (trpcEnabled) {
      if (!variables.Name) {
        throw new Error('Name is required');
      }

      return trpcMutation.insertDepartmentTypeGroupByName({
        Name: variables.Name,
      });
    }

    const result = await insertDepartmentTypeGroupByNameGraphQL({
      variables,
    });
    if (!result.data) {
      throw new Error('Failed to insert department type group');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDepartmentTypeGroupByName,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
      data: trpcMutation.data,
    };
  }

  return {
    insertDepartmentTypeGroupByName,
    loading: graphqlState.loading,
    error: graphqlState.error,
    data: graphqlState.data,
  };
};
